import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Caption1, DisplayCurrency, DisplayHours, InfoIcon, Tooltip } from "@activecollab/components";
import classNames from "classnames";
import PropTypes from "prop-types";
import { getJobTypeById } from "~/store/selectors/selector.jobTypes";
import { getUserById } from "~/store/selectors/selector.users";
import { ROW_CLASSES_ITEM, ROW_CLASSES_SHARED } from "./utils";

export const CostSummaryCostItem = ({
  id,
  type,
  hours,
  rate,
  currencyCode,
  defaultCurrency,
  internalCost,
  thousandSeparator,
  decimalSeparator,
  decimalSpaces,
  total,
  withoutInternalRate,
}) => {
  const { t } = useTranslation();
  const item = useSelector((state) => {
    if (type === "user") {
      return getUserById(state, id);
    }
    return getJobTypeById(state, id);
  });

  const name = type === "user" ? item?.display_name : item?.name;

  return (
    <div className={classNames(ROW_CLASSES_ITEM, ROW_CLASSES_SHARED)}>
      <Caption1
        color="secondary"
        weight="bold"
      >
        <DisplayHours
          value={hours}
          trimZeroMinutes={false}
          format="short"
          as="span"
          variant="Caption 1"
          color="secondary"
          weight="bold"
          decimalSeparator={decimalSeparator}
          decimalSpaces={decimalSpaces}
          thousandSeparator={thousandSeparator}
        />{" "}
        h
      </Caption1>
      <Caption1
        color="tertiary"
        className="tw-min-w-0 tw-flex tw-items-center tw-gap-1"
      >
        <div className="tw-truncate">{name}</div>
        {type === "job" ? (
          <div className="tw-shrink-0">
            @
            <DisplayCurrency
              variant="Caption 1"
              color="tertiary"
              currencyCode={currencyCode}
              currencyPosition="right"
              decimalSeparator={decimalSeparator}
              decimalSpaces={decimalSpaces}
              thousandSeparator={thousandSeparator}
              format="long"
              value={rate}
              trimDecimals={false}
              as="span"
            />
            /h
          </div>
        ) : null}
      </Caption1>
      <div className="tw-flex tw-gap-1.5 tw-text-right tw-items-center tw-justify-end">
        {withoutInternalRate ? (
          <Tooltip title={t("Internal rates missing")}>
            <InfoIcon
              fill="var(--red-alert)"
              className="tw-w-[18px] tw-h-[18px]"
            />
          </Tooltip>
        ) : null}
        <DisplayCurrency
          variant="Caption 1"
          weight="bold"
          align="right"
          as="span"
          color="secondary"
          currencyCode={defaultCurrency}
          currencyPosition="right"
          decimalSeparator={decimalSeparator}
          decimalSpaces={decimalSpaces}
          format="short"
          thousandSeparator={thousandSeparator}
          value={internalCost}
          trimDecimals={false}
        />
      </div>
      <DisplayCurrency
        variant="Caption 1"
        weight="bold"
        align="right"
        color="secondary"
        currencyCode={currencyCode}
        currencyPosition="right"
        decimalSeparator={decimalSeparator}
        decimalSpaces={decimalSpaces}
        format="short"
        thousandSeparator={thousandSeparator}
        value={total}
        trimDecimals={false}
      />
    </div>
  );
};

CostSummaryCostItem.propTypes = {
  id: PropTypes.number,
  type: PropTypes.oneOf(["job", "user"]),
  hours: PropTypes.number.isRequired,
  rate: PropTypes.number,
  currencyCode: PropTypes.string.isRequired,
  defaultCurrency: PropTypes.string.isRequired,
  internalCost: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  thousandSeparator: PropTypes.string,
  decimalSeparator: PropTypes.string,
  decimalSpaces: PropTypes.number,
  withoutInternalRate: PropTypes.bool,
};

CostSummaryCostItem.defaultProps = {
  decimalSeparator: ".",
  thousandSeparator: ",",
  decimalSpaces: 2,
  type: "job",
};
